<template>
  <div>
    <select-shop :loadingButton="loading" @getData="fetchFromApi" />

    <div v-if="isOrderBuffetEnabled">
      <!-- SUMMARY CARDS -->
      <CRow>
        <!-- Bills -->
        <CCol md="3" sm="6" class="d-flex">
          <CCard class="shadow-sm border-0 w-100" style="border-left:4px solid #20a8d8;">
            <CCardBody class="d-flex align-items-center justify-content-between">
              <div>
                <div class="text-muted small mb-1">
                  {{ $t("buffet.summary.bills") }}
                </div>
                <div class="h4 mb-0 text-primary">
                  {{ summary.totalBills || 0 }}
                </div>
                <div
                  v-if="summary.totalBills && summary.grandTotal"
                  class="text-muted small mt-1"
                >
                  {{ $t("buffet.summary.billsAvg", {
                    value: formatMoney(summary.grandTotal / summary.totalBills)
                  }) }}
                </div>
              </div>
              <CIcon name="cil-description" size="3xl" class="text-primary" />
            </CCardBody>
          </CCard>
        </CCol>

        <!-- Customers -->
        <CCol md="3" sm="6" class="d-flex">
          <CCard class="shadow-sm border-0 w-100" style="border-left:4px solid #39f;">
            <CCardBody class="d-flex align-items-center justify-content-between">
              <div>
                <div class="text-muted small mb-1">
                  {{ $t("buffet.summary.customers") }}
                </div>
                <div class="h4 mb-0 text-info">
                  {{ summary.totalCustomers || 0 }}
                </div>
                <div
                  v-if="summary.totalCustomers && summary.grandTotal"
                  class="text-muted small mt-1"
                >
                  {{ $t("buffet.summary.customersAvg", {
                    value: formatMoney(summary.grandTotal / summary.totalCustomers)
                  }) }}
                </div>
              </div>
              <CIcon name="cil-people" size="3xl" class="text-info" />
            </CCardBody>
          </CCard>
        </CCol>

        <!-- Grand Total -->
        <CCol md="3" sm="6" class="d-flex">
          <CCard class="shadow-sm border-0 w-100" style="border-left:4px solid #4dbd74;">
            <CCardBody class="d-flex align-items-center justify-content-between">
              <div>
                <div class="text-muted small">
                  <strong>{{ $t("buffet.summary.totalSales") }}</strong>
                </div>
                <div class="h5 mb-0 text-success">
                  {{ formatMoney(summary.grandTotal) }}
                </div>
              </div>
              <CIcon name="cil-dinner" size="3xl" class="text-success" />
            </CCardBody>
          </CCard>
        </CCol>

        <!-- Extra + Buffet -->
        <CCol md="3" sm="6" class="d-flex">
          <CCard class="shadow-sm border-0 w-100">
            <CCardBody>
              <CRow>
                <CCol md="6">
                  <div class="text-muted small mb-1">
                    {{ $t("buffet.summary.extraSales") }}
                  </div>
                  <div class="h4 mb-0 text-warning">
                    {{ formatMoney(summary.extraTotal) }}
                  </div>
                  <div
                    v-if="summary.grandTotal"
                    class="text-muted small mt-1"
                  >
                    {{ $t("buffet.summary.extraRatio", {
                      value: extraShare.toFixed(1)
                    }) }}
                  </div>
                </CCol>

                <CCol md="6">
                  <div class="text-muted small mb-1">
                    {{ $t("buffet.summary.buffetSales") }}
                  </div>
                  <div class="h4 mb-0 text-success">
                    {{ formatMoney(summary.buffetTotal) }}
                  </div>
                  <div
                    v-if="summary.grandTotal"
                    class="text-muted small mt-1"
                  >
                    {{ $t("buffet.summary.buffetRatio", {
                      value: buffetShare.toFixed(1)
                    }) }}
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- CHARTS -->
      <CRow>
        <!-- BAR -->
        <CCol lg="8" md="12">
          <CCard class="shadow-sm">
            <CCardBody>
              <h4 class="font-weight-normal mb-1">
                {{ $t("buffet.charts.packageSales") }}
              </h4>
              <p class="text-muted mb-3">
                {{ $t("buffet.charts.compare") }}
              </p>

              <CChartBar
                :datasets="barData.datasets"
                :labels="barData.labels"
                :options="barOptions"
              />
            </CCardBody>
          </CCard>
        </CCol>

        <!-- DONUT -->
        <CCol lg="4" md="12">
          <CCard class="shadow-sm">
            <CCardBody>
              <h4 class="font-weight-normal mb-1">
                {{ $t("buffet.charts.packageGrand") }}
              </h4>
              <p class="text-muted mb-3">
                {{ $t("buffet.charts.sameTheme") }}
              </p>

              <CChartDoughnut
                :datasets="pieData.datasets"
                :labels="pieData.labels"
                :options="pieOptions"
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- TABLE -->
      <CCard class="shadow-sm">
        <CCardHeader>
          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">
              {{ $t("buffet.table.title") }}
            </h4>
            <small class="text-muted">
              {{ $t("buffet.table.count", { value: packages.length }) }}
            </small>
          </div>
        </CCardHeader>

        <CCardBody>
          <DataTable
            :items="tableItems"
            :fields="tableFields"
            hover
            striped
            bordered
            small
            clickableRows
            style="white-space: nowrap"
            @row-clicked="goToPackageDetail"
          >
            <template #actions="{ item }">
              <td class="text-right">
                <CButton
                  size="sm"
                  color="primary"
                  variant="outline"
                  @click="goToPackageDetail(item)"
                >
                  {{ $t("buffet.table.action") }}
                </CButton>
              </td>
            </template>
          </DataTable>

          <pagination
            :meta_data="meta_data"
            @next="fetchPackageStatisticByBill"
          />
        </CCardBody>
      </CCard>

      <!-- HOURLY -->
      <CRow class="mt-4">
        <CCol lg="12">
          <CCard class="shadow-sm">
            <CCardBody>
              <h4 class="font-weight-normal mb-2">
                {{ $t("buffet.charts.hourlyCustomers") }}
              </h4>
              <small class="text-muted">
                {{ $t("buffet.charts.peakHint") }}
              </small>

              <CChartLine
                :datasets="hourlyLineData.datasets"
                :labels="hourlyLineData.labels"
                :options="hourlyLineOptions"
                style="height:260px;"
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>

    <!-- FEATURE DISABLED -->
    <div v-else class="text-center">
      <CCard>
        <CCardBody>
          <img
            src="/img/BF-banner.jpg"
            alt="Feature Not Available"
            style="width: -webkit-fill-available;"
          />
        </CCardBody>
      </CCard>
    </div>
  </div>
</template>


<script>
import { CChartBar, CChartDoughnut ,CChartLine} from "@coreui/vue-chartjs";
import pos from "@/services/service";
import util from "@/util/util";
import moment from "moment";
import DataTable from '@/containers/DataTable'
import { mapGetters } from "vuex";
import Pagination from '@/containers/Pagination'
import local from "@/services/local";

export default {
  name: "BuffetPackageSummaryTab",
  components: { CChartBar, CChartDoughnut,CChartLine ,DataTable,Pagination},

  data() {
    return {
      loading: true,
      summary: {
        totalBills: 0,
        totalCustomers: 0,
        buffetTotal: 0,
        extraTotal: 0,
        grandTotal: 0,
        netAmount: 0,
        vat: 0
      },
      meta_data: {
          last_page: null,
          current_page: 1,
          prev_page_url: null,
          items: 0,
          limit: null,
          itemCount: null,
      },
      packages: [],

      /* 🎨 Minimal Pastel Theme */
      baseColors: [
        ["#A8DADC", "#D7EFF0"],
        ["#457B9D", "#AFC6D6"],
        ["#FFB4A2", "#FFE1D8"],
        ["#BFD7EA", "#E7F3FA"],
        ["#F6BD60", "#F9DDAE"],
        ["#84A59D", "#C7D7D3"],
        ["#F28482", "#F8C6C5"],
        ["#6D6875", "#B9B5BD"],
        ["#FFCAD4", "#FFE6EA"],
        ["#C6DEF1", "#E5F2FA"],
        ["#D0E6A5", "#ECF6D7"],
        ["#E2C2FF", "#F2E6FF"]
      ],
      hourlyStats: [],
      isOrderBuffetEnabled : false,
    };
  },

  created() {
   this.getOrderShopSetting()
    this.fetchFromApi()
  },

  computed: {
    ...mapGetters(["date"]),

    /* ===== SUMMARY ===== */
    buffetShare () {
      if (!this.summary.grandTotal) return 0
      return (this.summary.buffetTotal / this.summary.grandTotal) * 100
    },

    extraShare () {
      if (!this.summary.grandTotal) return 0
      return (this.summary.extraTotal / this.summary.grandTotal) * 100
    },

    /* ===== TABLE ===== */
    tableFields () {
      return [
        {
          key: "no",
          label: "#",
          _classes: "text-dark font-weight-normal"
        },
        {
          key: "packageName",
          label: this.$t("buffet.table.name"),
          _classes: "text-dark font-weight-normal"
        },
        {
          key: "bills",
          label: this.$t("buffet.table.bills"),
          _classes: "text-right text-dark font-weight-normal"
        },
        {
          key: "customers",
          label: this.$t("buffet.table.customers"),
          _classes: "text-right text-dark font-weight-normal"
        },
        {
          key: "buffetTotalText",
          label: this.$t("buffet.table.buffet"),
          _classes: "text-right text-dark font-weight-normal"
        },
        {
          key: "extraTotalText",
          label: this.$t("buffet.table.extra"),
          _classes: "text-right text-dark font-weight-normal"
        },
        {
          key: "grandTotalText",
          label: this.$t("buffet.table.total"),
          _classes: "text-right text-dark font-weight-normal"
        },
        {
          key: "actions",
          label: this.$t("buffet.table.action"),
          _classes: "text-right text-dark font-weight-normal"
        }
      ]
    },

    tableItems () {
      return this.packages.map((p, idx) => ({
        no: idx + 1,
        ...p,
        buffetTotalText: this.formatMoney(p.buffetTotal),
        extraTotalText: this.formatMoney(p.extraTotal),
        grandTotalText: this.formatMoney(p.grandTotal)
      }))
    },

    /* ===== BAR CHART ===== */
    barData () {
      return {
        labels: this.packages.map(p => p.packageName),
        datasets: [
          {
            label: this.$t("buffet.label.buffet"),
            data: this.packages.map(p => p.buffetTotal),
            backgroundColor: this.packages.map(
              (_, i) => this.baseColors[i % this.baseColors.length][0]
            )
          },
          {
            label: this.$t("buffet.label.extra"),
            data: this.packages.map(p => p.extraTotal),
            backgroundColor: this.packages.map(
              (_, i) => this.baseColors[i % this.baseColors.length][1]
            )
          }
        ]
      }
    },

    barOptions () {
      const vm = this
      return {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true } }]
        },
        tooltips: {
          mode: "index",
          callbacks: {
            label (tooltipItem, data) {
              return (
                data.datasets[tooltipItem.datasetIndex].label +
                ": " +
                vm.formatMoney(tooltipItem.yLabel) +
                " ฿"
              )
            }
          }
        },
        legend: { position: "bottom" }
      }
    },

    /* ===== DONUT ===== */
    pieData () {
      return {
        labels: this.packages.map(p => p.packageName),
        datasets: [
          {
            backgroundColor: this.packages.map(
              (_, i) => this.baseColors[i % this.baseColors.length][0]
            ),
            data: this.packages.map(p => p.grandTotal)
          }
        ]
      }
    },

    pieOptions () {
      return {
        maintainAspectRatio: false,
        legend: { position: "right" }
      }
    },

    /* ===== HOURLY BAR ===== */
    hourlyBarData () {
      return {
        labels: this.hourlyStats.map(h => h.label),
        datasets: [
          {
            label: this.$t("buffet.label.buffet"),
            backgroundColor: "#A8DADC",
            data: this.hourlyStats.map(h => h.packageAmount)
          },
          {
            label: this.$t("buffet.label.extra"),
            backgroundColor: "#FFCAD4",
            data: this.hourlyStats.map(h => h.extraAmount)
          }
        ]
      }
    },

    hourlyBarOptions () {
      const vm = this
      return {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true,
            ticks: { autoSkip: true, maxTicksLimit: 12 }
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true,
              callback (value) {
                return vm.formatMoney(value)
              }
            }
          }]
        },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            title (items) {
              return vm.$t("common.time") + " " + items[0].label
            },
            label (tooltipItem, data) {
              const ds = data.datasets[tooltipItem.datasetIndex]
              return `${ds.label}: ${vm.formatMoney(tooltipItem.yLabel)} ฿`
            }
          }
        },
        legend: { position: "bottom" }
      }
    },

    /* ===== HOURLY LINE ===== */
    hourlyLineData () {
      return {
        labels: this.hourlyStats.map(i => i.label),
        datasets: [
          {
            label: this.$t("buffet.label.customer"),
            data: this.hourlyStats.map(i => i.customer),
            borderColor: "#6BCBBD",
            backgroundColor: "rgba(107,203,189,0.2)",
            pointBackgroundColor: "#6BCBBD",
            pointBorderColor: "#6BCBBD",
            borderWidth: 2,
            fill: true,
            lineTension: 0.25,
            pointRadius: 3
          }
        ]
      }
    },

    hourlyLineOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: v => String(v)
            },
            gridLines: { color: "#f1f3f5" }
          }]
        },
        legend: { display: false }
      }
    }
  },

  methods: {
    async fetchFromApi () {
      this.loading = false
      await Promise.all([
        this.getOrderShopSetting(),
        this.fetchPackageStatisticByBill(),
        this.fetchSummaryByShop(),
        this.fetchPackageStatisticByHour()
      ])
      this.loading = true
    },
    async fetchSummaryByShop() {
      try {
        const shopObjectId = localStorage.getItem("shopObjectId");
        const startAt = moment(this.date.start).format("YYYY-MM-DD");
        const endAt = moment(this.date.end).format("YYYY-MM-DD");

        const res = await pos({
          url: "/api/v3.0/package/shop/statistics",
          method: "get",
          params: { shopObjectId, startAt, endAt },
          headers: { shopObjectId }
        });

        const s = res.data.data?.[0] || {};

        this.summary = {
          totalBills: s.bill || 0,
          totalCustomers: s.customer || 0,
          buffetTotal: s.packageAmount || 0,
          extraTotal: s.extraAmount || 0,
          grandTotal: s.grandTotal || 0,
          netAmount: s.netAmount || 0,
          vat: s.vat || 0
        };
      } catch (err) {
        console.error(err);
      }
    },
    async fetchPackageStatisticByHour () {
      try {
        const shopObjectId = localStorage.getItem('shopObjectId')
        const startAt = moment(String(this.date.start)).format("YYYY-MM-DD")
        const endAt   = moment(String(this.date.end)).format("YYYY-MM-DD")

        const params = { shopObjectId, startAt, endAt }
        const headers = { shopObjectId: util.sanitizeShopObjectId(shopObjectId) }

        const res = await pos({
          url: "/api/v3.0/package/hour/statistics",
          method: "get",
          params,
          headers
        })

        const rows = Array.isArray(res.data?.data) ? res.data.data : []

        this.hourlyStats = rows.map(r => {
          const hour = Number(r._id)
          return {
            hour,
            label: `${String(hour).padStart(2,'0')}:00`,
            customer: Number(r.customer) || 0
          }
        }).sort((a, b) => a.hour - b.hour)

      } catch (e) {
        console.error("fetchPackageStatisticByHour", e)
        this.hourlyStats = []
      }
    },
     fetchPackageStatisticByBill(page = 1) {
      const shopObjectId = localStorage.getItem("shopObjectId");
      const startAt = moment(this.date.start).format("YYYY-MM-DD");
      const endAt = moment(this.date.end).format("YYYY-MM-DD");

      pos({
        url: "/api/v3.0/package/bill/statistics",
        method: "get",
        params: { shopObjectId, startAt, endAt ,page: page, limit: 10 },
        headers: { shopObjectId }
      }).then((res) => {
        this.applyApiData(res.data.data);
        if (res.data.paginate.items == undefined) {
            this.meta_data.items = 0;
        } else {
            this.meta_data.items = res.data.paginate.items;
        }
        this.meta_data.last_page = res.data.paginate.pageCount;
        this.meta_data.current_page = res.data.paginate.currentPage;
        this.meta_data.itemCount = res.data.paginate.itemCount;
        this.meta_data.limit = res.data.paginate.perPage;

      });
    },

    applyApiData(rows) {
      if (!Array.isArray(rows)) rows = [];

      this.packages = rows
        .filter((r) => r._id)
        .map((r) => ({
          packageId: r._id,
          packageName: r.name,
          bills: r.bill,
          customers: r.customer,
          buffetTotal: r.packageAmount,
          extraTotal: r.extraAmount,
          grandTotal: r.grandTotal
        }));
    },

    goToPackageDetail(item) {
      console.log(item);
      
      this.$router.push({
        name: "PackageReportDetail",
        query: { packageId: item.packageId, packageName: item.packageName }
      });
    },

    formatMoney(n) {
      return (Number(n) || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    getOrderShopSetting(){
      const shopObjectId = `${localStorage.getItem('shopObjectId')}`
      let params = {
          shopObjectId: shopObjectId,
      }
      const headers = {
          shopObjectId: util.sanitizeShopObjectId(shopObjectId),
      }
      local({
          url: "/api/v3.0/shop/ordershopsetting",
          params: params,
          headers: headers,
          method: "get",
      }).then((res) => {
          if(res.status ==200){
              let isOrderBuffetEnabled  = res.data.data.isOrderBuffetEnabled
              if(isOrderBuffetEnabled!== undefined && isOrderBuffetEnabled !== null && isOrderBuffetEnabled!==''){
                  this.isOrderBuffetEnabled = isOrderBuffetEnabled
              }else{
                  this.isOrderBuffetEnabled = false
              }
          }else{
              this.isOrderBuffetEnabled = false
          }
          
      }).catch((error) => {
              console.log(error);
      });
    },
  }
};
</script>
